/* GET and POST: auth */

import { AppRoutes, backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import type { AxiosError } from "axios";
import axios from "axios";
import type {
  LoginApiResponse,
  LoginErrorResponse,
  LoginResponse,
  ResendEmailPayload,
} from "./types/login.types";
import { isBusinessSelectionResponse } from "./types/login.types";
import { setLastBusinessLogin } from "@/utils/cookieUtils";

const LOGIN_URL = "api/auth/token/";
const GOOGLE_OAUTH_URL = "api/auth/google/";
const GOOGLE_CONFIG_URL = "api/auth/google/config/";
const RESEND_EMAIL_URL = "api/auth/resend-verify-email/";
const RESEND_VERIFY_URL = "api/auth/resend-verify/";
const UNLINK_GOOGLE_URL = "api/auth/google/unlink/";
const SET_PASSWORD_URL = "api/auth/set-password/";
const GLOBAL_RESOLVER_URL = "api/auth/resolver/";

/**
 *
 * @param data a JSON object with email, password, businessSlug, and optional skipRedirect
 * @param setNotification a function to set the notification
 * sets the access token and refresh token and logs the user in
 * sets the notification to display upon successful login, errors, and loading
 */
export const login = async (data: {
  email: string;
  password: string;
  businessSlug?: string;
  skipRedirect?: boolean;
}): Promise<LoginApiResponse> => {
  try {
    const response = await axios.post<LoginApiResponse>(
      `${backend_url}/${LOGIN_URL}`,
      data,
      {
        ...AXIOS_CONFIG,
      }
    );

    if (response.status === 200) {
      const responseData = response.data;

      // Backend is asking the user to pick a business — return early
      if (isBusinessSelectionResponse(responseData)) {
        return responseData;
      }

      sessionStorage.clear();
      localStorage.clear();

      // Store last business login for convenience (only if login successful and no actions required)
      if (
        !responseData.content.requiresAction ||
        Object.keys(responseData.content.requiresAction).length === 0
      ) {
        if (data.businessSlug && responseData.content.user?.businessName) {
          try {
            setLastBusinessLogin(
              data.businessSlug,
              responseData.content.user.businessName
            );
          } catch (error) {
            console.error("Error setting last business login cookie:", error);
          }
        }
      }

      // Skip redirect if explicitly requested (e.g., during onboarding)
      if (data.skipRedirect) {
        return responseData;
      }

      if (
        !responseData.content.requiresAction ||
        Object.keys(responseData.content.requiresAction).length === 0
      ) {
        window.location.href = AppRoutes.root;
      }
    }
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;

    if (axiosError.response?.data) {
      throw axiosError.response.data; // throw structured error so caller can handle
    }

    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};
/**
 * Resends the verification email based on the provided email.
 * @param {object} payload - an object with email as a key
 * @param {function} setNotification - a function to set the notification
 * @returns {Promise<any>} a promise that resolves to the response from the backend
 * @throws Will throw an error if the request fails
 */
export const ResendEmail = async (
  payload: ResendEmailPayload,
  setNotification: any
): Promise<any> => {
  try {
    const response = await axios.post(
      `${backend_url}/${RESEND_EMAIL_URL}`,
      payload,
      {
        ...AXIOS_CONFIG,
      }
    );
    if (response.status === 200) {
      setNotification({
        title: "Email Resend Succesfully",
        message: "Please check your email to verify your account",
        color: "green",
      });
    } else {
      setNotification({
        title: "Resend Email Failed",
        message:
          "Email resend failed. Please try again later or contact support: help@smartwiz.io.",
        color: "red",
      });
      return response;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const resendVerification = async (email: string): Promise<any> => {
  try {
    const response = await axios.post(
      `${backend_url}/${RESEND_VERIFY_URL}`,
      { email },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error resending verification:", error);
    throw error;
  }
};

export const checkAuthStatus = async (): Promise<LoginResponse> => {
  try {
    const response = await axios.get<LoginResponse>(
      `${backend_url}/api/auth/token/me/`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;

    if (axiosError.response?.data) {
      throw axiosError.response.data; // throw structured error so caller can handle
    }

    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};

export const checkTokenAuthStatus = async (): Promise<LoginResponse> => {
  try {
    const response = await axios.get<LoginResponse>(
      `${backend_url}/api/auth/token/check/`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;

    if (axiosError.response?.data) {
      throw axiosError.response.data; // throw structured error so caller can handle
    }

    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};

/**
 * Authenticates user with Google OAuth ID token.
 * Handles both login (existing user) and account creation (new user).
 * @param {object} data - Google OAuth data
 * @param {string} data.idToken - Google OAuth ID token
 * @param {string} [data.nonce] - Nonce from ID token for replay attack prevention
 * @param {string} [data.businessSlug] - Business identifier for access control
 * @param {string} [data.userType] - Type of user being created (owner, staff, client)
 * @param {string} [data.businessId] - Business ID for new account creation
 * @param {string} [data.firstName] - First name from Google
 * @param {string} [data.lastName] - Last name from Google
 * @param {string} [data.phoneNumber] - Phone number (may not be available from Google)
 * @param {string} [data.referrerId] - Referral ID from referral link
 * @param {string} [data.token] - Team member invitation token
 * @param {string} [data.assigneeId] - Assignee ID for client auto-assignment
 * @param {boolean} [data.allowCreate] - Whether to allow creating new users (default: true)
 * @param {boolean} [data.skipRedirect] - Skip automatic redirect (e.g., during onboarding)
 * @returns {Promise<LoginApiResponse>} Returns login response or business selection response
 * @throws Will throw an error if authentication fails
 */
export const googleOAuth = async (data: {
  idToken: string;
  nonce?: string;
  businessSlug?: string;
  userType?: string;
  businessId?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  referrerId?: string;
  token?: string;
  assigneeId?: string;
  allowCreate?: boolean;
  skipRedirect?: boolean;
}): Promise<LoginApiResponse> => {
  try {
    const response = await axios.post<LoginApiResponse>(
      `${backend_url}/${GOOGLE_OAUTH_URL}`,
      data,
      {
        ...AXIOS_CONFIG,
      }
    );

    if (response.status === 200) {
      const responseData = response.data;

      // Backend is asking the user to pick a business — return early
      if (isBusinessSelectionResponse(responseData)) {
        return responseData;
      }

      sessionStorage.clear();
      localStorage.clear();

      // Store last business login for convenience (only if login successful and no actions required)
      if (
        !responseData.content.requiresAction ||
        Object.keys(responseData.content.requiresAction).length === 0
      ) {
        if (data.businessSlug && responseData.content.user?.businessName) {
          try {
            setLastBusinessLogin(
              data.businessSlug,
              responseData.content.user.businessName
            );
          } catch (error) {
            console.error("Error setting last business login cookie:", error);
          }
        }
      }

      // Skip redirect if explicitly requested (e.g., during onboarding)
      if (data.skipRedirect) {
        return responseData;
      }

      if (
        !responseData.content.requiresAction ||
        Object.keys(responseData.content.requiresAction).length === 0
      ) {
        window.location.href = AppRoutes.root;
      }
    }
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;

    if (axiosError.response?.data) {
      throw axiosError.response.data; // throw structured error so caller can handle
    }

    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};

/**
 * Fetches the Google OAuth Client ID from the backend.
 * This is used to initialize Google Identity Services in the frontend.
 * @returns {Promise<string>} Returns the Google Client ID or empty string if not configured
 * @throws Will throw an error if the request fails
 */
export const getGoogleClientId = async (): Promise<string> => {
  try {
    const response = await axios.get<{
      message: string;
      content: { clientId: string };
    }>(`${backend_url}/${GOOGLE_CONFIG_URL}`, {
      ...AXIOS_CONFIG,
    });

    if (response.status === 200 && response.data.content?.clientId) {
      return response.data.content.clientId;
    }
    return "";
  } catch (error) {
    console.error("Error fetching Google Client ID:", error);
    return "";
  }
};

/**
 * Exchanges an admin login session token for JWT tokens with httponly cookies.
 * This is used for admin one-click login functionality.
 * @param {string} sessionToken - The one-time session token from the admin login URL
 * @returns {Promise<{ success: boolean; redirectPath: string }>} Returns the redirect path on success
 * @throws Will throw an error if the token exchange fails
 */
export const exchangeAdminLoginToken = async (
  sessionToken: string,
  searchParams: string
): Promise<{ success: boolean; redirectPath: string }> => {
  try {
    // Extract redirect parameter from URL query string
    const urlParams = new URLSearchParams(searchParams);
    const redirectPath = urlParams.get("redirect") || "/";

    console.log("[Admin Login] Query params:", searchParams);
    console.log("[Admin Login] Extracted redirect path:", redirectPath);

    // Drop the OUTGOING identity's cached state before the cookie swap — the
    // same wipe `login` does. Impersonation only changes the auth cookie, so
    // without this the previous user's `businessInfo` / `personalInfo` survive
    // and any screen seeded from storage renders THEIR firm under the new
    // session. Saving such a form writes one firm's details onto another's
    // business (prod, 2026-09-16).
    sessionStorage.clear();
    localStorage.clear();

    // Navigate directly to the backend endpoint with redirect parameter
    // Backend will set cookies via HTTP redirect, which Safari accepts more reliably
    const exchangeUrl = `${backend_url}/api/ext/admin-login/${sessionToken}/?redirect=${encodeURIComponent(
      redirectPath
    )}`;

    // Use window.location to trigger a full page navigation
    // This ensures Safari treats it as a first-party navigation and accepts cookies
    window.location.href = exchangeUrl;

    // Return a promise that never resolves since we're navigating away
    return new Promise(() => {});
  } catch (error) {
    console.error("Error exchanging session token:", error);
    throw error;
  }
};

/**
 * Unlinks a Google account from the current user and sets a password.
 * The password must be provided and will be set before unlinking to ensure
 * the user can still authenticate. If password setting fails, the unlinking is rolled back.
 * Requires a valid Google ID token to verify the user controls the Google account.
 * @param {string} newPassword - The new password to set before unlinking  // pragma: allowlist secret
 * @param {string} idToken - Google ID token to verify account ownership
 * @returns {Promise<void>} Resolves when Google account is unlinked and password is set
 * @throws Will throw an error if the request fails
 */
export const unlinkGoogleAccount = async (
  newPassword: string,
  idToken: string
): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/${UNLINK_GOOGLE_URL}`,
      { newPassword, idToken },
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;
    if (axiosError.response?.data) {
      throw axiosError.response.data;
    }
    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};

/**
 * Sets an initial password for a user who doesn't have one.
 * This is typically used after unlinking a Google account.
 * @param {string} newPassword - The new password to set  // pragma: allowlist secret
 * @returns {Promise<void>} Resolves when password is set successfully
 * @throws Will throw an error if the request fails
 */
export const setInitialPassword = async (
  newPassword: string
): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/${SET_PASSWORD_URL}`,
      { newPassword },
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;
    if (axiosError.response?.data) {
      throw axiosError.response.data;
    }
    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};

/**
 * Submits email to Global Resolver to receive business-specific sign-in links.
 * This endpoint never reveals whether the email exists or how many businesses it belongs to.
 * @param {string} email - Email address to resolve
 * @returns {Promise<{ message: string }>} Always returns success message (enumeration-safe)
 * @throws Will throw an error if the request fails
 */
export const submitGlobalResolver = async (
  email: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.post<{ message: string }>(
      `${backend_url}/${GLOBAL_RESOLVER_URL}`,
      { email: email.toLowerCase() },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<LoginErrorResponse>;
    if (axiosError.response?.data) {
      throw axiosError.response.data;
    }
    const err = new Error("An unknown error occurred. Please try again.");
    (err as any).error = "unknown_error";
    (err as any).details = "An unknown error occurred. Please try again.";
    throw err;
  }
};

// Resolver token verification removed - resolver links now redirect to business login
// where users must enter their password for security (two-factor verification)
